import React, { useState, useCallback, useEffect } from 'react';
import { AlertTriangle, Lightbulb } from 'lucide-react';
import { Button } from 'antd';
import WorkflowEnhancer from './WorkflowEnhancer';
import { useDispatch, useSelector } from 'react-redux';
import {
	selectConflictCount,
	selectConflicts,
	selectImprovementCount,
	selectImprovements,
} from '@/store/workflowsAnalysis/selector';
import {
	applyImprovement,
	rejectImprovement,
	removeConflict,
} from '@/store/workflowsAnalysis/slice';
import FunCopilotButton from './FunCopilotButton';

interface WorkflowIssuesFloatingButtonsProps {
	onCopilotClick?: () => void;
	showCopilot?: boolean;
}

const WorkflowIssuesFloatingButtons: React.FC<WorkflowIssuesFloatingButtonsProps> = ({
	onCopilotClick,
	showCopilot,
}) => {
	const [drawerVisible, setDrawerVisible] = useState(false);
	const [activeTab, setActiveTab] = useState<'conflicts' | 'improvements'>('conflicts');
	const [dismissedConflicts, setDismissedConflicts] = useState<Set<string>>(new Set<string>());
	const [dismissedImprovements, setDismissedImprovements] = useState<Set<string>>(
		new Set<string>(),
	);

	const dispatch = useDispatch();

	// Get data from selectors
	const conflicts = useSelector(selectConflicts);
	const improvements = useSelector(selectImprovements);
	const conflictCount = useSelector(selectConflictCount);
	const improvementCount = useSelector(selectImprovementCount);

	const showConflicts = useCallback(() => {
		setActiveTab('conflicts');
		setDrawerVisible(true);
	}, []);

	const showImprovements = useCallback(() => {
		setActiveTab('improvements');
		setDrawerVisible(true);
	}, []);

	const closeDrawer = useCallback(() => {
		setDrawerVisible(false);
	}, []);

	const handleApplyImprovement = useCallback(
		(workflowId: string, scenarioIndex: number, newDescription: string) => {
			try {
				dispatch(
					applyImprovement({
						workflowId,
						scenarioIndex,
						newDescription,
					}),
				);
				if (improvements.length === 1) {
					closeDrawer();
				}
			} catch (error) {
				console.error('Error applying improvement:', error);
			}
		},
		[dispatch, improvements.length, closeDrawer],
	);

	const handleRejectImprovement = useCallback(
		(workflowId: string, scenarioIndex: number) => {
			try {
				dispatch(
					rejectImprovement({
						workflowId,
						scenarioIndex,
					}),
				);
				if (improvements.length === 1) {
					closeDrawer();
				}
			} catch (error) {
				console.error('Error rejecting improvement:', error);
			}
		},
		[dispatch, improvements.length, closeDrawer],
	);

	const handleRemoveConflict = useCallback(
		(
			scenarioDescription: string,
			conflictingWorkflowId: string,
			conflictingScenarioIndex: number,
		) => {
			try {
				dispatch(
					removeConflict({
						scenarioDescription,
						conflictingWorkflowId,
						conflictingScenarioIndex,
					}),
				);
				if (conflicts.length === 1 && conflicts[0].similarPrompts.length === 1) {
					closeDrawer();
				}
			} catch (error) {
				console.error('Error removing conflict:', error);
			}
		},
		[dispatch, conflicts, closeDrawer],
	);

	useEffect(() => {
		if (drawerVisible && conflictCount === 0 && improvementCount === 0) {
			closeDrawer();
		}
	}, [conflictCount, improvementCount, drawerVisible, closeDrawer]);

	if (conflictCount === 0 && improvementCount === 0) {
		{
			showCopilot && <FunCopilotButton onClick={onCopilotClick} />;
		}
	}

	const handleDismissConflict = (conflictId: string) => {
		setDismissedConflicts((prev) => new Set(prev).add(conflictId));
	};

	const handleDismissImprovement = (improvementId: string) => {
		setDismissedImprovements((prev) => new Set(prev).add(improvementId));
	};

	return (
		<>
			<div className="fixed bottom-9 right-8 flex flex-col sm:flex-row gap-4 z-50">
				{showCopilot && <FunCopilotButton onClick={onCopilotClick} />}
				{conflictCount > 0 && (
					<Button
						type="default"
						size="large"
						icon={<AlertTriangle size={16} />}
						onClick={showConflicts}
						className="w-[280px] sm:w-[240px] h-12 rounded-xl bg-red-500 border-none shadow-lg shadow-red-500/30 text-white font-semibold transition-all duration-300 overflow-hidden hover:-translate-y-0.5 hover:shadow-xl hover:shadow-red-500/40 group"
					>
						<div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
						<span className="relative z-10">Fix Conflicting Prompts</span>
					</Button>
				)}

				{improvementCount > 0 && (
					<Button
						type="default"
						size="large"
						icon={<Lightbulb size={16} />}
						onClick={showImprovements}
						className="w-[280px] sm:w-[200px] h-12 rounded-xl bg-amber-500 border-none shadow-lg shadow-amber-500/30 text-white font-semibold transition-all duration-300 overflow-hidden hover:-translate-y-0.5 hover:shadow-xl hover:shadow-amber-500/40 group"
					>
						<div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
						<span className="relative z-10">
							Fix {improvementCount} Prompt{improvementCount !== 1 ? 's' : ''}
						</span>
					</Button>
				)}
			</div>

			{/* WorkflowEnhancer Drawer */}
			{drawerVisible && conflicts && improvements && (
				<WorkflowEnhancer
					conflicts={conflicts}
					improvements={improvements}
					onApplyImprovement={handleApplyImprovement}
					onRejectImprovement={handleRejectImprovement}
					onRemoveConflict={handleRemoveConflict}
					drawerVisible={drawerVisible}
					activeTab={activeTab}
					onClose={closeDrawer}
					dismissedConflicts={dismissedConflicts}
					dismissedImprovements={dismissedImprovements}
					onDismissConflict={handleDismissConflict}
					onDismissImprovement={handleDismissImprovement}
				/>
			)}
		</>
	);
};

export default WorkflowIssuesFloatingButtons;
